import React, { useEffect, useState } from 'react';
import { useGameLaunch } from './GameLaunchContext';
import styled from 'styled-components';
import { MoveLeftIcon } from 'lucide-react';
import socket from '../Socket';
import C from '../Constant';
import { decode, encode, forceSatoshiFormat, Event } from '../Helper';
import storage from '../Storage';

// Layout constants (matching Index page layout)
const SIDEBAR_WIDTH = 250;
const HEADER_HEIGHT = 63;
const BOTTOM_NAV_HEIGHT = 60;

const LoaderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #1a1d1e;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
`;

const Loader = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid #25ee89;
  border-top: 4px solid transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const GameContainer = styled.div`
  position: fixed;
  z-index: 666;
  background: #1a1d1e;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  /* Desktop: sit inside the content area (right of sidebar, below header) */
  top: ${HEADER_HEIGHT}px;
  left: ${SIDEBAR_WIDTH}px;
  width: calc(100vw - ${SIDEBAR_WIDTH}px);
  height: calc(100vh - ${HEADER_HEIGHT}px);

  /* Mobile: full screen by default, nav visible only for LuckySport */
  @media (max-width: 768px) {
    z-index: 9999;
    top: 0px;
    left: 0;
    width: 100vw;
    height: ${props => props.$luckysport
      ? `calc(100dvh - ${BOTTOM_NAV_HEIGHT}px)`
      : '100dvh'};
  }
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 6px 12px;
  background: #232626;
  border-bottom: 1px solid #333;
  flex-shrink: 0;
  height: 40px;

  @media (max-width: 768px) {
    height: 36px;
    padding: 4px 10px;
  }
`;

const BackButton = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  color: #25ee89;
  font-weight: 600;
  font-size: 14px;

  &:hover {
    opacity: 0.8;
  }
`;

const GameIframe = () => {
  const { gameUrl, isGameOpen, setIsGameOpen, isLuckySportGame, setIsLuckySportGame } = useGameLaunch();
  const [loading, setIsLoading] = useState(true);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Real-time wallet balance state
  const [walletBalance, setWalletBalance] = useState(storage.getKey('credit') || '0');
  const [walletCoin, setWalletCoin] = useState(storage.getKey('coin') || 'INR');

  // Listen for real-time wallet balance updates via socket
  useEffect(() => {
    const handleCreditData = (data) => {
      const decoded = decode(data);
      const { credit } = decoded;
      if (credit) {
        const currentCoin = (storage.getKey('coin') || 'INR').toLowerCase();
        const currentCredit = forceSatoshiFormat(credit[currentCoin]);
        setWalletBalance(currentCredit);
        storage.setKey('credit', currentCredit);
      }
    };

    const handleUpdateCredit = (data) => {
      const decoded = decode(data);
      const { value, coin: updatedCoin } = decoded;
      const formattedValue = forceSatoshiFormat(value);
      const currentCoin = (storage.getKey('coin') || 'INR').toLowerCase();

      if (updatedCoin && updatedCoin.toLowerCase() === currentCoin) {
        setWalletBalance(formattedValue);
        storage.setKey('credit', formattedValue);
      }
    };

    socket.on(C.CREDIT, handleCreditData);
    socket.on(C.UPDATE_CREDIT, handleUpdateCredit);

    return () => {
      socket.off(C.CREDIT, handleCreditData);
      socket.off(C.UPDATE_CREDIT, handleUpdateCredit);
    };
  }, []);

  // Listen for storage/coin changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'credit') {
        setWalletBalance(e.newValue || '0');
      }
      if (e.key === 'coin') {
        setWalletCoin(e.newValue || 'INR');
        socket.emit(C.CREDIT, encode({ coin: e.newValue || 'INR' }));
      }
    };
    window.addEventListener('storage', handleStorageChange);

    const handleCoinChanged = (newCoin) => {
      setWalletCoin(newCoin || 'INR');
      const newBalance = storage.getKey('credit') || '0';
      setWalletBalance(newBalance);
      socket.emit(C.CREDIT, encode({ coin: newCoin || 'INR' }));
    };

    Event.on('coin_changed', handleCoinChanged);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      Event.off('coin_changed', handleCoinChanged);
    };
  }, []);

  // Reset states when game closes
  useEffect(() => {
    if (!isGameOpen) {
      setIsLoading(true);
      setIframeLoaded(false);
    }
  }, [isGameOpen]);

  // When gameUrl is set, show loading until iframe loads
  useEffect(() => {
    if (gameUrl && isGameOpen) {
      setIsLoading(true);
      setIframeLoaded(false);
    }
  }, [gameUrl, isGameOpen]);

  // Track screen size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle iframe load completion
  const handleIframeLoad = () => {
    setIframeLoaded(true);
    setIsLoading(false);
  };

  if (!isGameOpen) return null;

  // Show loader in the content area while waiting for game URL
  if (!gameUrl) {
    return (
      <GameContainer $luckysport={isLuckySportGame ? 1 : 0}>
        <LoaderOverlay>
          <Loader />
        </LoaderOverlay>
      </GameContainer>
    );
  }

  return (
    <GameContainer $luckysport={isLuckySportGame ? 1 : 0}>
      {/* Mobile back button - shown for all games on mobile */}
      {isMobile && (
        <div
          onClick={() => {
            setIsGameOpen(false);
            setIsLuckySportGame(false);
            // Re-fetch latest balance without reloading the page
            socket.emit(C.CREDIT, encode({ coin: storage.getKey('coin') || 'INR' }));
          }}
          style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            zIndex: 10001,
            background: 'black',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <MoveLeftIcon size={20} color="#fff" />
        </div>
      )}

      {/* Loader overlay while iframe is loading */}
      {loading && (
        <LoaderOverlay>
          <Loader />
        </LoaderOverlay>
      )}

      {/* Game iframe */}
      <iframe
        src={gameUrl}
        title="Game"
        onLoad={handleIframeLoad}
        style={{
          width: '100%',
          flex: 1,
          border: 'none',
          margin: 0,
          padding: 0,
          overflow: 'hidden',
          visibility: loading ? 'hidden' : 'visible',
        }}
        allowFullScreen
      />
    </GameContainer>
  );
};

export default GameIframe;
